import { SECTION_VARIANTS, type SectionType, type WebsiteDefinition } from "@baze/schema";
import { RENDER_TIME_KEYS, type ContentBrief, type Design, type Template } from "./types";

/**
 * Deterministic template instantiation: Template + Design + business input (+ optional AI brief)
 * -> Website Definition. The Go API (internal/generator) implements the SAME algorithm; keep both
 * in sync with the rules below (documented in packages/catalog/README.md#instantiation).
 */
export interface InstantiateInput {
  name: string;
  industryName: string;
  city?: string;
  address?: string;
  whatsapp?: string;
  /** User-provided offerings from the wizard (names + optional prices). */
  offerings?: Array<{ name: string; price?: number }>;
}

type Json = null | boolean | number | string | Json[] | { [k: string]: Json };

export function substitute(value: Json, vars: Record<string, string>, key?: string): Json {
  if (typeof value === "string") {
    if (key && (RENDER_TIME_KEYS as readonly string[]).includes(key)) return value;
    return value.replace(/\{\{\s*(nama_usaha|kota|kategori)\s*\}\}/g, (_, k: string) => vars[k] ?? "");
  }
  if (Array.isArray(value)) return value.map((v) => substitute(v, vars));
  if (value && typeof value === "object") {
    const out: Record<string, Json> = {};
    for (const [k, v] of Object.entries(value)) out[k] = substitute(v, vars, k);
    return out;
  }
  return value;
}

const clampSeo = (seo: { title: string; description: string }) => ({ title: clampText(seo.title, 70), description: clampText(seo.description, 170) });

export function resolveVariant(type: SectionType, templateVariant: string | undefined, design: Design): string {
  const allowed = SECTION_VARIANTS[type] as readonly string[];
  if (templateVariant && allowed.includes(templateVariant)) return templateVariant;
  const fromDesign = design.variants[type];
  if (fromDesign && allowed.includes(fromDesign)) return fromDesign;
  return allowed[0]!;
}

/** Clamp to max chars at a word boundary, appending "…". Only used for SEO text (Go mirrors this). */
export function clampText(value: string, max: number): string {
  const chars = Array.from(value.trim());
  if (chars.length <= max) return chars.join("");
  const cut = chars.slice(0, max - 1).join("");
  const space = cut.lastIndexOf(" ");
  return (space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,.;:–-]+$/u, "") + "…";
}

const nonEmpty = (s: string | undefined): s is string => typeof s === "string" && s.trim().length > 0;

export function instantiateTemplate(
  template: Template,
  design: Design,
  input: InstantiateInput,
  brief?: ContentBrief,
): WebsiteDefinition {
  const vars = { nama_usaha: input.name, kota: input.city || "kota Anda", kategori: input.industryName };

  const pages = template.pages.map((page) => ({
    id: page.id,
    slug: page.slug,
    title: page.title,
    seo: clampSeo(substitute(page.seo as Json, vars) as { title: string; description: string }),
    sections: page.sections.map((s) => ({
      id: s.id,
      type: s.type,
      variant: resolveVariant(s.type, s.variant, design),
      anchor: s.anchor,
      ...(s.navLabel ? { navLabel: s.navLabel } : {}),
      ...(s.showInNav !== undefined ? { showInNav: s.showInNav } : {}),
      ...(s.tone ? { tone: s.tone } : {}),
      ...(s.motion ? { style: { motion: s.motion as Json } } : {}),
      content: substitute(s.content as Json, vars) as Record<string, Json>,
    })),
  }));

  const home = pages.find((p) => p.slug === "/") ?? pages[0]!;
  const first = (type: SectionType) => home.sections.find((s) => s.type === type)?.content;
  const set = (obj: Record<string, Json> | undefined, key: string, value: string | undefined) => {
    if (obj && nonEmpty(value)) obj[key] = value.trim();
  };

  // Offerings: user-provided names/prices win; AI descriptions are matched by index.
  const userOfferings = (input.offerings ?? []).filter((o) => nonEmpty(o.name));
  const aiOfferings = brief?.offerings ?? [];
  const offerings =
    userOfferings.length > 0
      ? userOfferings.map((o, i) => ({ name: o.name.trim(), price: o.price, description: aiOfferings[i]?.description }))
      : aiOfferings.map((o) => ({ name: o.name, price: undefined as number | undefined, description: o.description }));

  if (brief) {
    const hero = first("hero");
    set(hero, "eyebrow", brief.hero.eyebrow);
    set(hero, "title", brief.hero.title);
    set(hero, "description", brief.hero.description);
    const about = first("about");
    set(about, "title", brief.about.title);
    set(about, "body", brief.about.body);
    const faq = first("faq");
    if (faq && brief.faq.length > 0) faq.items = brief.faq.map((f) => ({ question: f.question, answer: f.answer }));
    const cta = first("cta");
    set(cta, "title", brief.cta.title);
    set(cta, "description", brief.cta.description);
    if (nonEmpty(brief.seo.title) && nonEmpty(brief.seo.description)) home.seo = clampSeo({ title: brief.seo.title, description: brief.seo.description });
  }

  if (offerings.length > 0) {
    const target = home.sections.find((s) => s.type === "menu" || s.type === "services" || s.type === "products");
    if (target) {
      const withOpt = <T extends Record<string, Json>>(base: T, price?: number, description?: string) => ({
        ...base,
        ...(price !== undefined ? { price } : {}),
        ...(nonEmpty(description) ? { description: description.trim() } : {}),
      });
      if (target.type === "menu") {
        const cats = (target.content.categories as Array<{ name: string }> | undefined) ?? [];
        target.content.categories = [
          { name: cats[0]?.name ?? "Menu", items: offerings.map((o) => withOpt({ name: o.name }, o.price, o.description)) },
        ];
      } else if (target.type === "services") {
        target.content.items = offerings.map((o) => withOpt({ title: o.name }, o.price, o.description));
      } else {
        const templItems = (target.content.items as Array<{ image?: Json }> | undefined) ?? [];
        target.content.items = offerings.map((o, i) => {
          const img = templItems.length > 0 ? templItems[i % templItems.length]?.image : undefined;
          return withOpt({ name: o.name, ...(img ? { image: img } : {}) }, o.price, o.description);
        });
      }
    }
  }

  const tagline = brief && nonEmpty(brief.tagline) ? brief.tagline : template.site.tagline ? (substitute(template.site.tagline, vars) as string) : undefined;

  const definition = {
    version: 1 as const,
    site: {
      name: input.name,
      ...(tagline ? { tagline } : {}),
      industry: template.industry,
      locale: "id-ID" as const,
      contact: {
        ...(input.whatsapp ? { whatsapp: input.whatsapp } : {}),
        ...(input.city ? { city: input.city } : {}),
        ...(input.address ? { address: input.address } : {}),
      },
      social: {},
      ...(template.site.hours ? { hours: template.site.hours } : {}),
      ...(template.site.whatsapp ? { whatsapp: template.site.whatsapp } : {}),
    },
    theme: { id: design.id, version: design.version },
    pages,
    meta: { source: brief ? ("ai" as const) : ("template" as const), templateId: template.id, templateVersion: template.version },
  };
  return definition as unknown as WebsiteDefinition;
}
